import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

export const COMMITMENT_LEVEL_OPTIONS: { value: number; label: string }[] = [
  { value: 4, label: 'Ready' },
  { value: 5, label: 'Thinking' },
  { value: 6, label: 'Not Ready' },
  { value: 7, label: 'Not Interested' },
];

export function getCommitmentLevelLabel(value: number | null | undefined): string | null {
  if (value == null) return null;
  return COMMITMENT_LEVEL_OPTIONS.find((o) => o.value === value)?.label ?? null;
}

/**
 * Map a stored gender value onto the intake form's Select options
 * ('Male' | 'Female' | 'Other'). Stored rows carry codes ('M'/'F') or
 * differently-cased words; anything unrecognised leaves the select empty rather
 * than holding a value the options list cannot render (which looked like
 * "gender not cloned" on both the master and the in-account prefill).
 */
export function normalizeGenderOption(value: string | null | undefined): string | null {
  const v = String(value ?? '').trim().toLowerCase();
  if (v === '') return null;
  if (v === 'm' || v === 'male') return 'Male';
  if (v === 'f' || v === 'female') return 'Female';
  if (v === 'o' || v === 'other' || v === 'x' || v === 'nonbinary' || v === 'non-binary') return 'Other';
  return null;
}

export interface IntakePatientPrefill {
  patientId: number;
  displayId: string | null;
  firstName: string;
  middleName: string | null;
  lastName: string;
  maidenName: string | null;
  email: string | null;
  birthDate: string | null;
  gender: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  /** The address country code ('US' / 'CA'); null when none is stored. */
  countryAbbr: string | null;
  cellPhone: string | null;
  /** The cell's stored dial code ('44'); null when none is stored. */
  cellCountryCode: string | null;
  facilityId: number | null;
  emailOptIn: boolean;
  smsOptIn: boolean;
  /** Marketing unsubscribe flags (patients_addons) — true = unsubscribed. */
  emailUnsubscribe: boolean;
  smsUnsubscribe: boolean;
}

interface RawPatientPrefill {
  patientID: number;
  displayID?: string | number | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  maidenName?: string | null;
  email?: string | null;
  birthDate?: string | null;
  gender?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  countryAbbr?: string | null;
  cellPhone?: string | null;
  cellCountryCode?: string | null;
  facilityID?: number | null;
  emailOptIn?: boolean | number | null;
  smsOptIn?: boolean | number | null;
  emailUnsubscribe?: boolean | number | null;
  smsUnsubscribe?: boolean | number | null;
}

export interface IntakePatientPlan {
  planId: number;
  planTypeId: number | null;
  planTypeName: string | null;
  name: string | null;
  stageName: string | null;
  date: string | null;
}

export interface SubmitLeadIntakePayload {
  /**
   * Which New Patient form settings apply (new patient, convert a lead, add a
   * plan). The server checks it against leadId/patientId and infers it when
   * absent.
   */
  formMode?: 'new' | 'convert_lead' | 'add_plan';
  leadId?: number;
  patientId?: number;
  allowDuplicatePlanType?: boolean;
  /**
   * Master patient record picked from the exact email/phone master match in the
   * Add New Patient form. New patients only; the server re-checks the match
   * against the submitted email/phone before linking.
   */
  masterPatientId?: number;
  /** Omitted when adding a plan: the server reads the existing patient's name. */
  firstName?: string;
  middleName?: string;
  lastName?: string;
  maidenName?: string;
  gender?: string;
  birthDate?: string;
  cellPhone?: string;
  /** The cell's dial code ('44'), never a countryID — distinct from the address `countryId`. */
  cellCountryCode?: string;
  email?: string;
  emailOptIn?: boolean;
  smsOptIn?: boolean;
  emailUnsubscribe?: boolean;
  smsUnsubscribe?: boolean;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  countryId?: number;
  leadSourceId?: number;
  referralSourceId?: number;
  /**
   * The referring PATIENT's patientID — stored in `plans.refSourceID`. Separate from
   * referralSourceId (the specific lead source) and leadSourceId (the category); all
   * three are independent columns.
   */
  referringPatientId?: number;
  referralFirstname?: string;
  referralLastname?: string;
  doctorId?: number;
  coordinatorId?: number;
  nurseId?: number;
  facilityId?: number;
  planTypeId?: number;
  procedure1Id?: number;
  procedure2Id?: number;
  procedure3Id?: number;
  timeframe?: number;
  commitmentLevel?: number;
  revenueOpportunity?: number;
  notes?: string;
}

/** Outcome of the optional master-patient link (`masterPatientId` in the payload). */
export type MasterLinkStatus =
  | 'linked'
  | 'off'
  | 'master_not_found'
  | 'account_mismatch'
  | 'no_exact_match'
  | 'patient_linked_elsewhere'
  | 'master_linked_in_account'
  | 'error';

export function describeMasterLinkStatus(status: MasterLinkStatus | string): string {
  switch (status) {
    case 'no_exact_match':
      return 'The saved email and phone no longer match the selected shared patient, so the patient was created standalone.';
    case 'master_linked_in_account':
      return 'That shared patient already has a record in this account. The new patient was created standalone.';
    case 'patient_linked_elsewhere':
      return 'This patient is already linked to a different shared patient.';
    case 'account_mismatch':
      return 'The selected shared patient belongs to a different organization.';
    case 'master_not_found':
      return 'The selected shared patient no longer exists.';
    case 'off':
      return 'Shared patient records are switched off on the server.';
    default:
      return 'The patient was created but could not be linked to the shared patient.';
  }
}

export interface SubmitLeadIntakeResponse {
  patientId: number;
  planId: number;
  /** Present only when the payload carried `masterPatientId` for a new patient. */
  masterPatientId?: number;
  masterLink?: MasterLinkStatus;
  /**
   * Codes for steps after the save that did not finish (`meta.warnings`). The
   * patient and plan are saved either way; warningMessages.ts turns each
   * code into a sentence for staff. Empty when everything finished.
   */
  warnings: string[];
}

function asNullableString(value: unknown): string | null {
  if (value == null) {
    return null;
  }
  const text = String(value).trim();
  return text === '' ? null : text;
}

function asNullableNumber(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function asBoolean(value: unknown): boolean {
  return value === true || value === 1 || value === '1';
}

function normalizePatient(raw: RawPatientPrefill): IntakePatientPrefill {
  return {
    patientId: Number(raw.patientID),
    displayId: asNullableString(raw.displayID),
    firstName: String(raw.firstName ?? '').trim(),
    middleName: asNullableString(raw.middleName),
    lastName: String(raw.lastName ?? '').trim(),
    maidenName: asNullableString(raw.maidenName),
    email: asNullableString(raw.email),
    birthDate: asNullableString(raw.birthDate),
    gender: asNullableString(raw.gender),
    address: asNullableString(raw.address),
    city: asNullableString(raw.city),
    state: asNullableString(raw.state),
    zip: asNullableString(raw.zip),
    countryAbbr: asNullableString(raw.countryAbbr),
    cellPhone: asNullableString(raw.cellPhone),
    cellCountryCode: asNullableString(raw.cellCountryCode),
    facilityId: asNullableNumber(raw.facilityID),
    emailOptIn: asBoolean(raw.emailOptIn),
    smsOptIn: asBoolean(raw.smsOptIn),
    emailUnsubscribe: asBoolean(raw.emailUnsubscribe),
    smsUnsubscribe: asBoolean(raw.smsUnsubscribe),
  };
}

export function getIntakePatient(patientId: number): Promise<IntakePatientPrefill> {
  return apiClient
    .get<ApiResponse<RawPatientPrefill>>(`/patients/${patientId}`)
    .then((response) => normalizePatient(response.data.data));
}

export function listPatientPlans(patientId: number): Promise<IntakePatientPlan[]> {
  return apiClient
    .get<ApiResponse<IntakePatientPlan[]>>(`/patients/${patientId}/plans`)
    .then((response) => response.data.data);
}

export function submitLeadIntake(payload: SubmitLeadIntakePayload): Promise<SubmitLeadIntakeResponse> {
  return apiClient
    .post<ApiResponse<Omit<SubmitLeadIntakeResponse, 'warnings'>>>('/leads/intake', payload)
    .then((response) => {
      const raw = response.data.meta?.warnings;
      const warnings = Array.isArray(raw) ? raw.filter((code): code is string => typeof code === 'string') : [];
      return { ...response.data.data, warnings };
    });
}
